import { createSelector } from '@reduxjs/toolkit';
import { TStoreState } from 'redux/types';

// ==========================================
// Base selector
// ==========================================

export const selectInvoiceManagement = (state: TStoreState) => state.invoiceManagement;

// ==========================================
// Invoice List selectors
// ==========================================

export const selectInvoiceList = (state: TStoreState) =>
	selectInvoiceManagement(state).invoiceList;

export const selectInvoiceListLoading = (state: TStoreState) =>
	selectInvoiceManagement(state).invoiceListLoading;

const EMPTY_INVOICE_ROWS: never[] = [];

export const selectInvoiceListData = createSelector(
	[selectInvoiceList],
	(invoiceList) => invoiceList?.rows ?? EMPTY_INVOICE_ROWS,
);

export const selectInvoiceListTotal = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.rowCount || 0;
};

export const selectInvoiceListPage = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page || null;
};

export const selectInvoiceListPageSize = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.size || 100;
};

export const selectInvoiceListPageCurrent = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.current || 1;
};

export const selectInvoiceListTotalPages = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.total || 0;
};

// ==========================================
// Download Invoice PDF
// ==========================================

export const selectDownloadInvoicePdfLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).downloadInvoicePdfLoader;

export const selectDownloadedInvoicePdf = (state: TStoreState) =>
	selectInvoiceManagement(state).downloadedInvoicePdf;

// ==========================================
// Download Invoice UDR CSV
// ==========================================

export const selectDownloadInvoiceUdrCsvLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).downloadInvoiceUdrCsvLoader;

export const selectDownloadedInvoiceUdrCsv = (state: TStoreState) =>
	selectInvoiceManagement(state).downloadedInvoiceUdrCsv;

// ==========================================
// Update Invoice Payment
// ==========================================

export const selectUpdateInvoicePaymentLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).updateInvoicePaymentLoader;

// ==========================================
// Update Invoice Comment
// ==========================================

export const selectUpdateInvoiceCommentLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).updateInvoiceCommentLoader;

// ==========================================
// Send Email For Invoice
// ==========================================

export const selectSendEmailForInvoiceLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).sendEmailForInvoiceLoader;

// ==========================================
// Update Invoice GPSR
// ==========================================

export const selectUpdateInvoiceGpsrLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).updateInvoiceGpsrLoader;

// ==========================================
// Encrypt Invoice Number
// ==========================================

export const selectEncryptInvoiceNumberLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).encryptInvoiceNumberLoader;

export const selectEncryptedInvoiceNumber = (state: TStoreState) =>
	selectInvoiceManagement(state).encryptInvoiceNumber;

// ==========================================
// Nullify Invoice
// ==========================================

export const selectNullifyInvoiceLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).nullifyInvoiceLoader;

// ==========================================
// Remove Invoice LPF
// ==========================================

export const selectRemoveInvoiceLpfLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).removeInvoiceLpfLoader;

// ==========================================
// Invoice List Excel Export
// ==========================================

export const selectInvoiceListExcelExportLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).invoiceListExcelExportLoader;

export const selectInvoiceListExcelExport = (state: TStoreState) =>
	selectInvoiceManagement(state).invoiceListExcelExport;

// Additional page data selectors
export const selectInvoiceListTotalAmountInEur = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.total_amount_in_eur_invoices || 0;
};

export const selectInvoiceListTotalInvoicedAmountInEur = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.total_invoiced_amount_in_eur_invoices || 0;
};

export const selectInvoiceListTotalPaidAmountInEur = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.total_paid_amount_in_eur || 0;
};

export const selectInvoiceListBalanceInEur = (state: TStoreState) => {
	const invoiceList = selectInvoiceList(state);
	return invoiceList?.page?.balance_in_eur || '0';
};

// ==========================================
// Download Invoice Archive
// ==========================================

export const selectDownloadInvoiceArchiveLoader = (state: TStoreState) =>
	selectInvoiceManagement(state).downloadInvoiceArchiveLoader;

// ==========================================
// Credit Notes
// ==========================================

export const selectCreditNotes = (state: TStoreState) => selectInvoiceManagement(state).creditNotes;

export const selectCreditNotesLoading = (state: TStoreState) =>
	selectInvoiceManagement(state).creditNotesLoading;

export const selectCreditNoteDetails = (state: TStoreState) =>
	selectInvoiceManagement(state).creditNoteDetails;

export const selectCreditNoteDetailsLoading = (state: TStoreState) =>
	selectInvoiceManagement(state).creditNoteDetailsLoading;
